/**
 * Deep links the bot sends to lot initiators look like
 * `https://t.me/<bot>/<app>?startapp=lot_<24-hex-ObjectId>`, which reaches the
 * Mini App as `tgWebAppStartParam === 'lot_<id>'`. Anything else (e.g. `debug`)
 * is not a route.
 */
const LOT_START_PARAM = /^lot_([a-f0-9]{24})$/

/** Returns the in-app route a start param points at, or null when it targets none. */
export function parseStartParam(startParam: string | null | undefined): string | null {
	const match = LOT_START_PARAM.exec(startParam ?? '')
	return match ? `/lot/${match[1]}` : null
}

/**
 * Inverse of {@link parseStartParam}: builds the outbound deep link the bot would
 * send for a lot, `https://t.me/<bot>/<app>?startapp=lot_<id>`, from the Mini App
 * base URL in `VITE_TG_MINIAPP_URL` (mirrors the backend's TELEGRAM_MINIAPP_URL).
 * Returns null when the base URL is not configured, so callers can hide the share
 * affordance rather than share a broken link.
 */
export function buildLotShareLink(lotId: string): string | null {
	const base = import.meta.env.VITE_TG_MINIAPP_URL?.replace(/\/$/, '')
	return base ? `${base}?startapp=lot_${lotId}` : null
}

/**
 * Module-level (not per-component) on purpose: the router is rebuilt whenever the
 * auth store's `user` changes, which remounts the whole tree — a per-component ref
 * would reset and hijack the user's navigation on every re-auth.
 */
let consumed = false

/** Same as {@link parseStartParam}, but yields a route only on the first call per app load. */
export function takeStartParamRoute(startParam: string | null | undefined): string | null {
	if (consumed) return null
	const route = parseStartParam(startParam)
	if (route) consumed = true
	return route
}
